import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, ShoppingBag, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { FinanceLayout, Pill, Empty, money } from './FinanceLayout';
import { useAuth } from '../../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  sku: '', name: '', description: '', collection: '', category: 'apparel',
  supplier: '', cost_per_unit_usd: '', retail_price_usd: '', image_url: '',
  variants: [], linked_tour_ids: [], status: 'draft', fulfillment_mode: 'print_on_demand', notes: '',
};

export default function FinanceMerchandisePage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [products, setProducts] = useState([]);
  const [tours, setTours] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, t] = await Promise.all([
        axios.get(`${API}/admin/finance/products`, { headers }),
        axios.get(`${API}/admin/tours`, { headers }),
      ]);
      setProducts(p.data);
      setTours(t.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        ...form,
        cost_per_unit_usd: Number(form.cost_per_unit_usd || 0),
        retail_price_usd: Number(form.retail_price_usd || 0),
      };
      if (editingId) {
        await axios.put(`${API}/admin/finance/products/${editingId}`, body, { headers });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/admin/finance/products`, body, { headers });
        toast.success('Product added');
      }
      setForm(EMPTY); setEditingId(null); setShowForm(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const edit = (p) => {
    setForm({ ...EMPTY, ...p });
    setEditingId(p.product_id); setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const del = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/admin/finance/products/${id}`, { headers });
      toast.success('Deleted'); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const actions = (
    <button onClick={() => { setEditingId(null); setForm(EMPTY); setShowForm(!showForm); }} data-testid="toggle-product-form" className="bg-white text-blue-700 px-4 py-2 rounded-full font-bold shadow-lg hover:scale-105 transition flex items-center gap-2 text-sm">
      <Plus className="w-4 h-4" /> {showForm ? 'Close' : 'New product'}
    </button>
  );

  return (
    <FinanceLayout title="Merchandise" subtitle={`${products.length} total`} actions={actions}>
      <div className="bg-gradient-to-r from-orange-50 to-rose-50 border border-orange-200 rounded-xl p-4 flex items-start gap-3">
        <ShoppingBag className="w-5 h-5 text-orange-700 mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <p className="font-bold text-orange-900">Merch catalog · ready for launch</p>
          <p className="text-orange-800 mt-1">Add products here, link them to tours, and track margins. When you're ready to sell, connect Printful or Shopify under <strong>Settings → Integrations</strong> and orders will sync automatically.</p>
        </div>
      </div>

      {showForm && (
        <form onSubmit={save} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid="product-form">
          <h3 className="font-black text-slate-900">{editingId ? 'Edit product' : 'New product'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <L label="Product name" req><I value={form.name} onChange={v => setForm({ ...form, name: v })} testid="prod-name" /></L>
            <L label="SKU"><I value={form.sku} onChange={v => setForm({ ...form, sku: v })} testid="prod-sku" /></L>
            <L label="Collection"><I value={form.collection} onChange={v => setForm({ ...form, collection: v })} testid="prod-collection" placeholder="e.g. Tel Aviv Origins" /></L>
            <L label="Category">
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white" data-testid="prod-category">
                <option value="apparel">Apparel</option>
                <option value="accessory">Accessory</option>
                <option value="print">Print / Poster</option>
                <option value="other">Other</option>
              </select>
            </L>
            <L label="Supplier / print vendor"><I value={form.supplier} onChange={v => setForm({ ...form, supplier: v })} placeholder="Printful / Printify / in-house" testid="prod-supplier" /></L>
            <L label="Fulfillment">
              <select value={form.fulfillment_mode} onChange={e => setForm({ ...form, fulfillment_mode: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white" data-testid="prod-fulfillment">
                <option value="print_on_demand">Print-on-demand</option>
                <option value="stocked">Self-stocked</option>
              </select>
            </L>
            <L label="Cost per unit (USD)" req><I type="number" value={form.cost_per_unit_usd} onChange={v => setForm({ ...form, cost_per_unit_usd: v })} testid="prod-cost" /></L>
            <L label="Retail price (USD)" req><I type="number" value={form.retail_price_usd} onChange={v => setForm({ ...form, retail_price_usd: v })} testid="prod-retail" /></L>
            <L label="Status">
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white" data-testid="prod-status">
                <option value="draft">Draft</option>
                <option value="live">Live</option>
                <option value="discontinued">Discontinued</option>
              </select>
            </L>
            <L label="Image URL"><I value={form.image_url} onChange={v => setForm({ ...form, image_url: v })} testid="prod-image" /></L>
            <L label="Linked tours" spans={2}>
              <select multiple value={form.linked_tour_ids} onChange={e => setForm({ ...form, linked_tour_ids: Array.from(e.target.selectedOptions).map(o => o.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white" size={Math.min(5, tours.length || 1)} data-testid="prod-tours">
                {tours.map(t => <option key={t.tour_id} value={t.tour_id}>{t.title}</option>)}
              </select>
            </L>
            <L label="Description" spans={2}><I value={form.description} onChange={v => setForm({ ...form, description: v })} testid="prod-desc" /></L>
          </div>
          {form.cost_per_unit_usd && form.retail_price_usd && (
            <div className="text-sm bg-slate-50 rounded-lg p-3 border border-slate-200">
              <strong>Projected margin:</strong> {money(Number(form.retail_price_usd) - Number(form.cost_per_unit_usd))} ({Math.round((1 - Number(form.cost_per_unit_usd) / Number(form.retail_price_usd)) * 100)}%)
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setShowForm(false); setForm(EMPTY); setEditingId(null); }} className="px-4 py-2 rounded-full bg-slate-100 font-bold text-sm">Cancel</button>
            <button type="submit" disabled={busy} data-testid="prod-submit" className="px-5 py-2 rounded-full bg-gradient-to-r from-teal-500 to-blue-600 text-white font-bold text-sm disabled:opacity-50">
              {busy ? 'Saving…' : editingId ? 'Update' : 'Add product'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-teal-500 border-t-transparent" /></div>
      ) : products.length === 0 ? (
        <Empty text="No products yet. Add your first Audiostride merch — a Tel Aviv Tour Tee is a great first drop." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(p => (
            <div key={p.product_id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`prod-card-${p.product_id}`}>
              {p.image_url ? (
                <img src={p.image_url} alt={p.name} className="w-full h-40 object-cover rounded-lg bg-slate-100" />
              ) : (
                <div className="w-full h-40 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-slate-400"><Tag className="w-10 h-10" /></div>
              )}
              <div className="mt-3 flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-black text-slate-900">{p.name}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{p.collection || p.category} {p.sku && `· ${p.sku}`}</p>
                </div>
                <Pill tone={p.status === 'live' ? 'emerald' : p.status === 'draft' ? 'slate' : 'rose'}>{p.status}</Pill>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <div><span className="text-slate-500">Cost </span><span className="font-bold">{money(p.cost_per_unit_usd)}</span></div>
                <div><span className="text-slate-500">Retail </span><span className="font-bold">{money(p.retail_price_usd)}</span></div>
                <Pill tone={p.margin_pct >= 40 ? 'emerald' : p.margin_pct >= 20 ? 'amber' : 'rose'}>{p.margin_pct}% margin</Pill>
              </div>
              <div className="mt-3 flex justify-end gap-1">
                <button onClick={() => edit(p)} data-testid={`edit-prod-${p.product_id}`} className="text-slate-500 hover:text-blue-600 p-1.5"><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => del(p.product_id)} data-testid={`del-prod-${p.product_id}`} className="text-slate-500 hover:text-rose-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </FinanceLayout>
  );
}

const L = ({ label, req, children, spans }) => (
  <label className={`block ${spans === 2 ? 'md:col-span-2' : ''}`}>
    <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">{label}{req && <span className="text-rose-500"> *</span>}</span>
    <div className="mt-1">{children}</div>
  </label>
);
const I = ({ value, onChange, type = 'text', testid, placeholder }) => (
  <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full border border-slate-300 rounded-lg px-3 py-2" data-testid={testid} />
);
